<template>
	<AppLayout :title="trans('menu.account')" :subtitle="trans('account.mandatory_help')">
		<template #actions>
			<PrimaryButton :color="Color.Green" @click.stop="update">
				<i class="fa-solid fa-check mr-2"/>
				{{ trans('general.save') }}
			</PrimaryButton>
		</template>
		<div v-if="forced" class="p-4">
			<Alert :color="Color.Blue">{{ trans('account.messages.update_info') }}</Alert>
		</div>
		<div class="grid grid-cols-2 gap-6">
			<div class="col-span-2 md:col-span-1">
				<div class="sticky top-40 flex flex-col gap-3">
					<Heading>{{ trans('account.edit.personal.title') }}</Heading>
					<SubHeading v-for="subtitle in trans('account.edit.personal.subtitles')">{{ subtitle }}</SubHeading>
				</div>
			</div>
			<div class="col-span-2 md:col-span-1 flex flex-col gap-5">
				<Card :title="trans('account.general.title')" :subtitle="trans('account.general.subtitle')">
					<LabeledObject full required>
						<template #label>{{ trans('account.create.type.title') }}</template>
						<select v-model="user.type">
							<option v-for="type in AccountType" :value="type">{{ trans(`account.type.${type}`) }}</option>
						</select>
					</LabeledObject>
					<LabeledObject full required>
						<template #label>{{ trans('account.first_name') }}</template>
						<input v-model="user.first_name" type="text">
					</LabeledObject>
					<LabeledObject full>
						<template #label>{{ trans('account.middle_name') }}</template>
						<input v-model="user.middle_name" type="text">
					</LabeledObject>
					<LabeledObject full required>
						<template #label>{{ trans('account.last_name') }}</template>
						<input v-model="user.last_name" type="text">
					</LabeledObject>
					<LabeledObject full required>
						<template #label>{{ trans('account.birthdate') }}</template>
						<IconDateInput v-model="user.birthdate" icon-class="fa-solid fa-cake" full/>
					</LabeledObject>
					<LabeledObject full required>
						<template #label>{{ trans('account.email') }}</template>
						<input v-model="user.email" type="email">
					</LabeledObject>
					<LabeledObject :description="trans('account.phone_help')" full required>
						<template #label>{{ trans('account.phone') }}</template>
						<PhoneNumber id="select-phone-country" v-model:phone="user.phone" v-model:code="user.phone_country_code" :countries="countries"/>
					</LabeledObject>
					<template v-if="isStudent">
						<LabeledObject full required>
							<template #label>{{ trans('account.gender.title') }}</template>
							<select v-model="user.gender">
								<option v-for="gender in Gender" :key="`gender-${gender}`" :value="gender">{{ trans(`account.gender.${gender}`) }}</option>
							</select>
						</LabeledObject>
						<LabeledObject v-if="user.gender === 'other'" full required>
							<template #label>{{ trans('general.specify') }}</template>
							<input v-model="user.other_gender" type="text">
						</LabeledObject>
					</template>
					<template v-if="isProfessional">
						<LabeledObject full>
							<template #label>{{ trans('account.institution') }}</template>
							<input v-model="user.institution" type="text">
						</LabeledObject>
						<LabeledObject full>
							<template #label>{{ trans('account.linkedin') }}</template>
							<IconTextInput v-model="user.linkedin" icon-class="fa-brands fa-linkedin" full/>
						</LabeledObject>
					</template>
				</Card>
				<Card :title="trans('account.citizenships.title')" :subtitle="trans('account.citizenships.subtitle')">
					<SearchCountry :label="trans('general.search')" required @selected="addCitizenship"/>
					<List>
						<ListItem v-for="country in user.citizenships" :title="country.name">
							<template #icon>
								<CountryFlag :iso="country.code" mode="rounded"/>
							</template>
							<template #accessory>
								<SoftButton :color="Color.Red" @click.stop="removeCitizenship(country)">
									<i class="fa-solid fa-trash"/>
								</SoftButton>
							</template>
						</ListItem>
					</List>
					<template v-if="isArgentine">
						<LabeledObject full required>
							<template #label>{{ trans('account.id_number') }}</template>
							<input v-model="user.id_number" type="text" @keyup="user.id_number = validateIdNumber(user.id_number)">
						</LabeledObject>
						<LabeledObject full required>
							<template #label>{{ trans('account.tax_id') }}</template>
							<input v-model="user.tax_id" type="text" @keyup="user.tax_id = validateTaxNumber(user.tax_id ?? '')">
						</LabeledObject>
					</template>
				</Card>
				<Card :title="trans('account.location.title')" :subtitle="trans('account.location.subtitle')">
					<SearchLocation v-model:location-id="user.location_id" :location="user.location" :countries="user.citizenships" :label="trans('general.search')" required/>
					<LabeledObject full required>
						<template #label>{{ trans('account.address') }}</template>
						<input v-model="user.address" type="text">
					</LabeledObject>
				</Card>
			</div>
			<div class="py-4 col-span-2">
				<hr class="dark:border-gray-800">
			</div>
			<div class="col-span-2 md:col-span-1">
				<div class="sticky top-40 flex flex-col gap-3">
					<Heading>{{ trans('account.edit.work_education.title') }}</Heading>
					<SubHeading v-for="subtitle in trans('account.edit.work_education.subtitles')">{{ subtitle }}</SubHeading>
				</div>
			</div>
			<div class="col-span-2 md:col-span-1 flex flex-col gap-5">
				<Switch v-model="user.work_region" :label="trans('account.work_region')" full/>
				<LabeledObject v-if="user.work_region" full>
					<template #label>{{ trans('account.work_region_place') }}</template>
					<select v-model="user.work_region_place">
						<option v-for="region in regions" :key="`work-region-place-${region.id}`" :value="region.id">{{ region.name }}</option>
					</select>
				</LabeledObject>
				<div class="flex items-center gap-3">
					<p class="grow text-xl">{{ trans('account.college_programs.title') }}</p>
					<PrimaryButton :color="Color.Green" @click.stop="showModal('new-college-program-modal')">
						<i class="fa-solid fa-plus"/>
					</PrimaryButton>
				</div>
				<Modal id="new-college-program-modal">
					<template #title>{{ trans('account.college_programs.add') }}</template>
					<div class="flex flex-col items-stretch gap-3">
						<LabeledObject full required>
							<template #label>{{ trans('account.college_programs.college') }}</template>
							<select v-model="newCollegeProgram.college_id">
								<option v-for="college in colleges" :key="`new-college-${college.id}`" :value="college.id">{{ college.name }}</option>
							</select>
						</LabeledObject>
						<LabeledObject full required>
							<template #label>{{ trans('account.college_programs.major') }}</template>
							<select v-model="newCollegeProgram.major_id">
								<option v-for="major in majors" :key="`new-major-${major.id}`" :value="major.id">{{ major.name }}</option>
							</select>
						</LabeledObject>
						<LabeledObject full required>
							<template #label>{{ trans('account.college_programs.status.title') }}</template>
							<select v-model="newCollegeProgram.status">
								<option v-for="status in CollegeProgramStatus" :key="`new-college-program-status-${status}`" :value="status">{{ trans(`account.college_programs.status.${status}`) }}</option>
							</select>
						</LabeledObject>
						<LabeledObject full required>
							<template #label>{{ trans('account.college_programs.credits_obtained') }}</template>
							<input v-model="newCollegeProgram.credits_obtained" type="number" min="0" step="1">
						</LabeledObject>
						<LabeledObject full required>
							<template #label>{{ trans('account.college_programs.credits_required') }}</template>
							<input v-model="newCollegeProgram.credits_required" type="number" min="0" step="1">
						</LabeledObject>
					</div>
					<template #footer>
						<PrimaryButton :color="Color.Green" @click.stop="addCollegeProgram">
							<i class="fa-solid fa-check mr-2"/>
							{{ trans('general.confirm') }}
						</PrimaryButton>
					</template>
				</Modal>
				<Card v-for="(collegeProgram, collegeProgramIndex) in user.college_programs" :key="`college-program-${collegeProgram.id}`" :title="collegeProgram.major.name" :subtitle="collegeProgram.college.name">
					<template #badges>
						<Badge v-if="collegeProgram.main" :color="Color.Indigo" active>{{ trans('account.college_programs.main') }}</Badge>
					</template>
					<LabeledObject full required>
						<template #label>{{ trans('account.college_programs.status.title') }}</template>
						<select v-model="collegeProgram.status">
							<option v-for="status in CollegeProgramStatus" :key="`new-college-program-status-${status}`" :value="status">{{ trans(`account.college_programs.status.${status}`) }}</option>
						</select>
					</LabeledObject>
					<LabeledObject full required>
						<template #label>{{ trans('account.college_programs.credits_obtained') }}</template>
						<input v-model="collegeProgram.credits_obtained" type="number" min="0" step="1">
					</LabeledObject>
					<LabeledObject full required>
						<template #label>{{ trans('account.college_programs.credits_required') }}</template>
						<input v-model="collegeProgram.credits_required" type="number" min="0" step="1">
					</LabeledObject>
					<template #footer>
						<SecondaryButton :color="Color.Green" @click.stop="updateCollegeProgram(collegeProgram)">
							<i class="fa-solid fa-save mr-2"/>
							{{ trans('general.save') }}
						</SecondaryButton>
						<SoftButton v-if="!collegeProgram.main" :color="Color.Blue" @click.stop="makeMain(collegeProgram.id)">{{ trans('account.college_programs.make_main') }}</SoftButton>
						<SoftButton v-if="user.college_programs.length > 1" :color="Color.Red" @click.stop="removeCollegeProgram(collegeProgramIndex)">
							<i class="fa-solid fa-trash mr-2"/>
							{{ trans('general.delete') }}
						</SoftButton>
					</template>
				</Card>
				<Card :title="trans('account.languages.title')" :subtitle="trans('account.languages.subtitle')">
					<List>
						<LanguageSelector
							v-for="language in languages"
							:key="`language-${language.id}-${user.languages.length}`"
							:language="language"
							:user-language="user.languages.find((l:Language) => l.id === language.id)"
							@toggle-language="toggleLanguage"
						/>
					</List>
				</Card>
			</div>
			<div class="py-4 col-span-2">
				<hr class="dark:border-gray-800">
			</div>
			<div class="col-span-2 md:col-span-1">
				<div class="sticky top-40 flex flex-col gap-3">
					<Heading>{{ trans('account.edit.healthcare.title') }}</Heading>
					<SubHeading v-for="subtitle in trans('account.edit.healthcare.subtitles')">{{ subtitle }}</SubHeading>
				</div>
			</div>
			<div class="col-span-2 md:col-span-1 flex flex-col gap-5">
				<Card :title="trans('account.emergency.title')" :subtitle="trans('account.emergency.subtitle')">
					<LabeledObject full required>
						<template #label>{{ trans('account.emergency.name') }}</template>
						<input v-model="user.emergency_name" type="text">
					</LabeledObject>
					<LabeledObject :description="trans('account.phone_help')" full required>
						<template #label>{{ trans('account.emergency.phone') }}</template>
						<PhoneNumber id="select-emergency-phone-country" v-model:phone="user.emergency_phone" v-model:code="user.emergency_phone_country_code" :countries="countries"/>
					</LabeledObject>
				</Card>
				<Card :title="trans('account.diets.title')" :subtitle="trans('general.select_applicable')">
					<List>
						<ListItem v-for="diet in diets" :key="`diet-${diet.id}`" :title="diet.name" @click.stop="toggleDiet(diet.id)">
							<template #accessory>
								<SoftButton v-if="user.diets_ids.includes(diet.id)" :color="Color.Red">
									<i class="fa-solid fa-trash"/>
								</SoftButton>
								<SoftButton v-else :color="Color.Green">
									<i class="fa-solid fa-plus"/>
								</SoftButton>
							</template>
						</ListItem>
					</List>
				</Card>
				<Card :title="trans('account.disabilities.cognitive.title')" :subtitle="trans('general.select_applicable')">
					<List>
						<ListItem v-for="disability in cognitiveDisabilities" :key="`cognitive-disability-${disability.id}`" :title="disability.name" @click.stop="toggleCognitiveDisability(disability.id)">
							<template #accessory>
								<SoftButton v-if="user.cognitive_disabilities_ids.includes(disability.id)" :color="Color.Red">
									<i class="fa-solid fa-trash"/>
								</SoftButton>
								<SoftButton v-else :color="Color.Green">
									<i class="fa-solid fa-plus"/>
								</SoftButton>
							</template>
						</ListItem>
					</List>
				</Card>
				<Card :title="trans('account.disabilities.physical.title')" :subtitle="trans('general.select_applicable')">
					<List>
						<ListItem v-for="disability in physicalDisabilities" :key="`physical-disability-${disability.id}`" :title="disability.name" @click.stop="togglePhysicalDisability(disability.id)">
							<template #accessory>
								<SoftButton v-if="user.physical_disabilities_ids.includes(disability.id)" :color="Color.Red">
									<i class="fa-solid fa-trash"/>
								</SoftButton>
								<SoftButton v-else :color="Color.Green">
									<i class="fa-solid fa-plus"/>
								</SoftButton>
							</template>
						</ListItem>
					</List>
				</Card>
				<Card :title="trans('account.disabilities.psychosocial.title')" :subtitle="trans('general.select_applicable')">
					<List>
						<ListItem v-for="disability in psychosocialDisabilities" :key="`psychosocial-disability-${disability.id}`" :title="disability.name" @click.stop="togglePsychosocialDisability(disability.id)">
							<template #accessory>
								<SoftButton v-if="user.psychosocial_disabilities_ids.includes(disability.id)" :color="Color.Red">
									<i class="fa-solid fa-trash"/>
								</SoftButton>
								<SoftButton v-else :color="Color.Green">
									<i class="fa-solid fa-plus"/>
								</SoftButton>
							</template>
						</ListItem>
					</List>
				</Card>
				<Card :title="trans('account.disabilities.sensory.title')" :subtitle="trans('general.select_applicable')">
					<List>
						<ListItem v-for="disability in sensoryDisabilities" :key="`sensory-disability-${disability.id}`" :title="disability.name" @click.stop="toggleSensoryDisability(disability.id)">
							<template #accessory>
								<SoftButton v-if="user.sensory_disabilities_ids.includes(disability.id)" :color="Color.Red">
									<i class="fa-solid fa-trash"/>
								</SoftButton>
								<SoftButton v-else :color="Color.Green">
									<i class="fa-solid fa-plus"/>
								</SoftButton>
							</template>
						</ListItem>
					</List>
				</Card>
				<Card :title="trans('account.other.title')" :subtitle="trans('account.other.subtitle')">
					<LabeledObject full>
						<template #label>{{ trans('account.other.provider') }}</template>
						<input v-model="user.healthcare" type="text">
					</LabeledObject>
					<LabeledObject full>
						<template #label>{{ trans('account.other.allergies') }}</template>
						<textarea v-model="user.allergies" class="resize-y"/>
					</LabeledObject>
				</Card>
			</div>
		</div>
	</AppLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import dayjs from 'dayjs'
import {route} from 'ziggy-js'
import utc from 'dayjs/plugin/utc'
import {trans} from 'laravel-vue-i18n'
import {type User} from '@/Types/User'
import {Color} from '@/Types/Enums/Color'
import {type Region} from '@/Types/Region'
import {Gender} from '@/Types/Enums/Gender'
import timezone from 'dayjs/plugin/timezone'
import {type Country} from '@/Types/Country'
import Card from '@/Components/Cards/Card.vue'
import List from '@/Components/Lists/List.vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import Modal from '@/Components/Modal/Modal.vue'
import {type Major} from '@/Types/Settings/Major'
import Badge from '@/Components/Badges/Badge.vue'
import Alert from '@/Components/Alerts/Alert.vue'
import Switch from '@/Components/Switch/Switch.vue'
import {type College} from '@/Types/Settings/College'
import {AccountType} from '@/Types/Enums/AccountType'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import ListItem from '@/Components/Lists/ListItem.vue'
import PhoneNumber from '@/Components/PhoneNumber.vue'
import {type Language} from '@/Types/Settings/Language'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import {LanguageLevel} from '@/Types/Enums/LanguageLevel'
import Heading from '@/Components/Typography/Heading.vue'
import LabeledObject from '@/Components/LabeledObject.vue'
import {type CollegeProgram} from '@/Types/CollegeProgram'
import CountryFlag from '@dzangolab/vue-country-flag-icon'
import SoftButton from '@/Components/Buttons/SoftButton.vue'
import {type Diet} from '@/Types/Association/Associates/Diet'
import {computed, type ComputedRef, type Ref, ref} from 'vue'
import SubHeading from '@/Components/Typography/SubHeading.vue'
import SearchCountry from '@/Components/Search/SearchCountry.vue'
import IconTextInput from '@/Components/Inputs/IconTextInput.vue'
import IconDateInput from '@/Components/Inputs/IconDateInput.vue'
import PrimaryButton from '@/Components/Buttons/PrimaryButton.vue'
import SearchLocation from '@/Components/Search/SearchLocation.vue'
import SecondaryButton from '@/Components/Buttons/SecondaryButton.vue'
import {CollegeProgramStatus} from '@/Types/Enums/CollegeProgramStatus'
import LanguageSelector from '@/Components/Association/Associates/LanguageSelector.vue'
import {type SensoryDisability} from '@/Types/Association/Associates/Disabilities/SensoryDisability'
import {type PhysicalDisability} from '@/Types/Association/Associates/Disabilities/PhysicalDisability'
import {type CognitiveDisability} from '@/Types/Association/Associates/Disabilities/CognitiveDisability'
import {type PsychosocialDisability} from '@/Types/Association/Associates/Disabilities/PsychosocialDisability'
import {showInfo, showModal, showSuccess, showValidationErrors, validateIdNumber, validateTaxNumber} from '@/helpers'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)
dayjs.tz.setDefault('America/Argentina/Buenos_Aires')

const props = defineProps<{
	user:User,
	forced:boolean,
	diets:Array<Diet>,
	majors:Array<Major>,
	regions:Array<Region>,
	colleges:Array<College>,
	countries:Array<Country>,
	languages:Array<Language>,
	sensoryDisabilities:Array<SensoryDisability>,
	physicalDisabilities:Array<PhysicalDisability>,
	cognitiveDisabilities:Array<CognitiveDisability>,
	psychosocialDisabilities:Array<PsychosocialDisability>,
}>()

const user:Ref<User> = ref(props.user)
const updating:Ref<boolean> = ref(false)
const diets:Ref<Array<Diet>> = ref(props.diets)
const majors:Ref<Array<Major>> = ref(props.majors)
const regions:Ref<Array<Region>> = ref(props.regions)
const colleges:Ref<Array<College>> = ref(props.colleges)
const countries:Ref<Array<Country>> = ref(props.countries)
const languages:Ref<Array<Language>> = ref(props.languages)
const sensoryDisabilities:Ref<Array<SensoryDisability>> = ref(props.sensoryDisabilities)
const physicalDisabilities:Ref<Array<PhysicalDisability>> = ref(props.physicalDisabilities)
const cognitiveDisabilities:Ref<Array<CognitiveDisability>> = ref(props.cognitiveDisabilities)
const psychosocialDisabilities:Ref<Array<PsychosocialDisability>> = ref(props.psychosocialDisabilities)
const newCollegeProgram:Ref<CollegeProgram> = ref({
	user_id: user.value.id,
	user: user.value,
	college_id: 0,
	major_id: 0,
	main: false,
	credits_obtained: 0,
	credits_required: 0,
	status: CollegeProgramStatus.Student,
} as CollegeProgram)

const isStudent:ComputedRef<boolean> = computed(():boolean => {
	return user.value.type === AccountType.Student
})

const isProfessional:ComputedRef<boolean> = computed(():boolean => {
	return !isStudent.value
})

const isArgentine:ComputedRef<boolean> = computed(() => {
	return user.value.citizenships.findIndex((c:Country) => c.id === 7) > -1
})

function toggleLanguage(language:Language):void{
	const index = user.value.languages.findIndex((l:Language) => l.id === language.id)
	if(index > -1){
		user.value.languages.splice(index, 1)
	} else {
		user.value.languages.push({
			id: language.id,
			name: language.name,
			pivot: {
				level: LanguageLevel.Basic,
			},
		} as Language)
	}
}

function addCitizenship(country:Country):void{
	if(!updating.value){
		updating.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('account.citizenships.add'), {
			country_id: country.id,
		}).then(() => {
			user.value.citizenships.push(country)
			showSuccess(trans('general.updated'))
		}).catch(showValidationErrors).finally(() => {
			updating.value = false
		})
	}
}

function removeCitizenship(country:Country):void{
	if(!updating.value){
		updating.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('account.citizenships.remove'), {
			country_id: country.id,
		}).then(() => {
			const index = user.value.citizenships.findIndex((c:Country) => c.id === country.id)
			if(index > -1){
				user.value.citizenships.splice(index, 1)
			}
			showSuccess(trans('general.updated'))
		}).catch(showValidationErrors).finally(() => {
			updating.value = false
		})
	}
}

function addCollegeProgram():void{
	if(!updating.value){
		updating.value = true
		showInfo(trans('general.inserting'))
		axios.post(route('account.college-programs.add'), newCollegeProgram.value).then(({data}) => {
			user.value.college_programs.push(data)
			newCollegeProgram.value.college_id = 0
			newCollegeProgram.value.major_id = 0
			newCollegeProgram.value.credits_obtained = 0
			newCollegeProgram.value.credits_required = 0
			newCollegeProgram.value.status = CollegeProgramStatus.Student
			showSuccess(trans('general.inserted'))
		}).catch(showValidationErrors).finally(() => {
			updating.value = false
		})
	}
}

function updateCollegeProgram(collegeProgram:CollegeProgram):void{
	if(!updating.value){
		updating.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('account.college-programs.update', collegeProgram.id), collegeProgram).then(() => {
			showSuccess(trans('general.updated'))
		}).catch(showValidationErrors).finally(() => {
			updating.value = false
		})
	}
}

function makeMain(collegeProgramId:number):void{
	if(!updating.value){
		updating.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('account.college-programs.make_main', collegeProgramId)).then(() => {
			showSuccess(trans('general.updated'))
			user.value.college_programs.forEach((program) => {
				program.main = program.id === collegeProgramId
			})
		}).catch(showValidationErrors).finally(() => {
			updating.value = false
		})
	}
}

function removeCollegeProgram(index:number):void{
	if(!updating.value && confirm(trans('general.confirm_action'))){
		updating.value = true
		showInfo(trans('general.deleting'))
		axios.delete(route('account.college-programs.remove', user.value.college_programs[index].id)).then(() => {
			user.value.college_programs.splice(index, 1)
			showSuccess(trans('general.deleted'))
		}).catch(showValidationErrors).finally(() => {
			updating.value = false
		})
	}
}

function toggleDiet(id:number):void{
	let index = user.value.diets_ids.findIndex(dietId => dietId === id)
	if(index === -1){
		user.value.diets_ids.push(id)
	} else {
		user.value.diets_ids.splice(index, 1)
	}
}

function toggleCognitiveDisability(id:number):void{
	let index = user.value.cognitive_disabilities_ids.findIndex(disabilityId => disabilityId === id)
	if(index === -1){
		user.value.cognitive_disabilities_ids.push(id)
	} else {
		user.value.cognitive_disabilities_ids.splice(index, 1)
	}
}

function togglePhysicalDisability(id:number):void{
	let index = user.value.physical_disabilities_ids.findIndex(disabilityId => disabilityId === id)
	if(index === -1){
		user.value.physical_disabilities_ids.push(id)
	} else {
		user.value.physical_disabilities_ids.splice(index, 1)
	}
}

function togglePsychosocialDisability(id:number):void{
	let index = user.value.psychosocial_disabilities_ids.findIndex(disabilityId => disabilityId === id)
	if(index === -1){
		user.value.psychosocial_disabilities_ids.push(id)
	} else {
		user.value.psychosocial_disabilities_ids.splice(index, 1)
	}
}

function toggleSensoryDisability(id:number):void{
	let index = user.value.sensory_disabilities_ids.findIndex(disabilityId => disabilityId === id)
	if(index === -1){
		user.value.sensory_disabilities_ids.push(id)
	} else {
		user.value.sensory_disabilities_ids.splice(index, 1)
	}
}

function update():void{
	if(!updating.value){
		updating.value = true
		showInfo(trans('general.updating'))
		axios.post(route('account.update', user.value.id), user.value).then(() => {
			showSuccess(trans('general.updated'))
		}).catch(showValidationErrors).finally(() => {
			updating.value = false
		})
	}
}
</script>
